import { useEffect, useRef, useState } from 'react'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import s from './Modal.module.css'

type ModalProps = {
  title: string
  open: boolean
  children: React.ReactNode
  onClose: () => void
  description?: string
}

export const Modal = ({
  title,
  open,
  children,
  onClose,
  description,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === dialogRef.current) {
      dialogRef.current?.close()
    }
  }

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      dialog.showModal()
    }
  }, [open])

  // Handle virtual keyboard on mobile
  useEffect(() => {
    if (!open) return

    const handleViewportChange = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height
        setKeyboardHeight(heightDiff > 0 ? heightDiff : 0)
      }
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange)
      handleViewportChange()

      return () => {
        window.visualViewport?.removeEventListener(
          'resize',
          handleViewportChange
        )
        setKeyboardHeight(0)
      }
    }
  }, [open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => onClose()
    dialog.addEventListener('close', handleClose)

    return () => dialog.removeEventListener('close', handleClose)
  }, [onClose])

  if (!open) return null

  return (
    <ErrorBoundary>
      <dialog
        ref={dialogRef}
        onClick={handleBackdropClick}
        className={`${s.modal} ${open ? s.visible : s.hidden}`}
        style={
          {
            '--keyboard-height': `${keyboardHeight}px`,
          } as React.CSSProperties
        }
      >
        <div className={s.content}>
          <h4 className={s.title}>{title}</h4>
          {description && <p className={s.description}>{description}</p>}
          <div className={s.children}>{children}</div>
          <button className={s.cancel} onClick={onClose}>
            Cancel
          </button>
        </div>
      </dialog>
    </ErrorBoundary>
  )
}
